/**
 * The folder rail (prototype `vMail`'s `.folders`).
 *
 * Every folder is real and flat now (Task 6) — `mail.get_emails` filters server-side, and every
 * `MailFolderSummary` carries `account: null` (see `MailPage`), so there is no per-account inbox
 * grouping to render any more.
 *
 * ---- The account switcher is REMOVED, not disabled ----
 *
 * `mail.get_mailbox_info` exists to say which Email Account is the customer side and which is the
 * supplier side, and on the live bench it answers `bosun_role: null` for both (defect B4).
 * Rather than guess — the wrong guess is a supplier reply silently filed as a customer enquiry —
 * this used to render a switcher, disabled, with an explanatory tooltip, sitting in the rail's
 * most valuable position: "All mail (account not confirmed)", permanently inert no matter what a
 * purchaser did. A control that can never become enabled is worse than no control — it reads as
 * broken, not as "coming soon" — so it's gone rather than hidden-but-still-there.
 *
 * To bring it back for real: reconnect `useMailboxInfo()`/`toMailboxInfo` (`lib/api/useMail.ts`,
 * unchanged and still covered by `useMail.test.ts` — only the call site here was removed) once
 * mailbox roles are actually set in Bosun Settings so `accounts` comes back non-empty, AND wire
 * the selected account through to `onSelect`/`useMailbox` so switching does something — today
 * even a confirmed role wouldn't be enough on its own, nothing downstream reads it.
 *
 * ---- The Inbox badge is not always a real unread count ----
 *
 * `MailPage` only knows Inbox's TRUE unread count while Inbox is the folder actually open — it
 * would otherwise need every row of every folder just to badge the rail, which is the eager 4x
 * fetch Task 6's review round 1 removed. When Inbox isn't open, `folder.unread` for it carries
 * `total` instead (see `MailPage`'s `folders` `useMemo`), and this component must not render
 * that as if it were a real unread number: `47` next to Inbox reads as "47 unread" to anyone who
 * has ever used a mail client, and it would quietly be wrong most of the time.
 *
 * `../../lib/mail.ts`'s `folderBadge` carries the "is this exact" signal — computed there (not
 * inline here) so `src/lib/mail.test.ts` can pin the behaviour without rendering this component,
 * matching how every other Mail derivation in this codebase is tested. Rendered with a `~` prefix
 * (visible without hovering — a `title` alone fails anyone who can't or doesn't hover) plus a
 * dimmed style and an explanatory tooltip, rather than hidden outright: a stand-in total is still
 * a real, useful number ("there's stuff in here"), just not the unread count it would be mistaken
 * for unlabelled.
 *
 * BACKEND GAP worth sending to the Frappe team: the folder rail needs an unread count per folder
 * WITHOUT fetching the rows — `get_emails` has no such aggregate today (verified against the
 * documented live shape: `{data, total?, limit?, offset?}`, no unread figure anywhere in it).
 */

import { Button } from '../../components/ui'
import { FOLDER_ICON, folderBadge, sameFolder, selectionOf, type FolderSelection } from '../../lib/mail'
import type { MailFolderSummary } from '../../lib/types'

const APPROX_INBOX_TOOLTIP =
  'This is the total message count, not unread — the unread count is only known while Inbox is the open folder (get_emails has no per-folder unread total).'

export function FolderRail({
  folders,
  selected,
  onSelect,
  onCompose,
}: {
  folders: MailFolderSummary[]
  selected: FolderSelection
  onSelect: (selection: FolderSelection) => void
  onCompose: () => void
}) {
  const shared = folders

  const item = (folder: MailFolderSummary) => {
    const selection = selectionOf(folder)
    const badge = folderBadge(folder, selected)
    return (
      <button
        type="button"
        key={`${folder.key}-${folder.account ?? 'shared'}`}
        className={`fitem${sameFolder(selection, selected) ? ' active' : ''}`}
        onClick={() => onSelect(selection)}
      >
        <span>{FOLDER_ICON[folder.key]}</span> {folder.title}
        {badge ? (
          <span
            className={`fc${badge.approx ? ' fc-approx' : ''}`}
            title={badge.approx ? APPROX_INBOX_TOOLTIP : undefined}
          >
            {badge.approx ? `~${badge.count}` : badge.count}
          </span>
        ) : null}
      </button>
    )
  }

  return (
    <div className="folders">
      <Button variant="primary" className="newmail" onClick={onCompose}>
        ✚ New mail
      </Button>

      {/* The account switcher used to render here, permanently disabled — removed rather than
          left dead; see the module header ("The account switcher is REMOVED, not disabled") for
          what brings it back. */}

      {shared.map(item)}
    </div>
  )
}
